import * as React from 'react';
import {
  Box,
  CircularProgress,
  Grid2,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSession } from '@talkjs/react';
import { Virtuoso } from 'react-virtuoso';
import { Message } from 'talkjs/all';
import { AxiosError } from 'axios';
import PageLayout from '../global/PageLayout';
import useGetConversations from '../../hooks/chat/useGetConversations';
import InboxMessage from './InboxMessage';
import ChatBox from './ChatBox';
import { ChatFilter, UserConversation } from '../../types/chats';
import { ROUTES } from '../../constants/global';
import useGetConversation from '../../hooks/chat/useGetConversation';
import { getConversation } from '../../services/chats';
import {
  updateConversationInList,
  updateExistingConversation,
} from '../../helpers/chats';
import InboxFilters from './InboxFilters';
import useChatFilters from './useChatFilters';
import { ChatProvider } from '../../context/ChatContext';
import ColoredAlert from '../global/ColoredAlert';
import { isAxiosRateLimitError } from '../../services/api';

function Loader() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%">
      <CircularProgress color="primary" size={20} />
    </Box>
  );
}

function Empty() {
  const theme = useTheme();
  return (
    <Box
      mt={2}
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%">
      <Typography variant="body2" color={theme.palette.medGrey.main}>
        No conversations found
      </Typography>
    </Box>
  );
}

const BREAKPOINT = 'md';

function ChatInbox() {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const session = useSession();

  const { filter } = useChatFilters();
  const [selectedConversation, setSelectedConversation] =
    React.useState<UserConversation | null>(null);

  const onSelectConversation = React.useCallback(
    (conversation: UserConversation | null) => {
      setSelectedConversation(prev =>
        prev?.id === conversation?.id ? null : conversation,
      );
      if (
        conversationId ||
        searchParams.get('appointment') ||
        searchParams.get('request')
      ) {
        navigate(ROUTES.chat, {
          replace: true,
          preventScrollReset: true,
        });
      }
    },
    [conversationId, navigate, searchParams],
  );

  const {
    conversations: allConversations = [],
    queryKey,
    hasNextPage,
    isFetching,
    isLoading,
    fetchNextPage,
    refetch,
    error,
  } = useGetConversations();
  const { conversation: paramConversation } =
    useGetConversation(conversationId);

  const currentConversation = React.useMemo(
    () =>
      allConversations.find(c => c.id === selectedConversation?.id) ||
      selectedConversation ||
      null,
    [allConversations, selectedConversation],
  );

  const onNewChatMessage = React.useCallback(
    async (data: Message) => {
      try {
        if (!filter || [ChatFilter.ALL, ChatFilter.UNREADS].includes(filter)) {
          const existingConversation = allConversations.find(
            c => c.id === data.conversation.id,
          );
          const updatedConversation: UserConversation = existingConversation
            ? updateExistingConversation(existingConversation, data)
            : await getConversation(data.conversation.id);
          if (!updatedConversation) return;
          updateConversationInList(queryKey, updatedConversation, true);
        } else {
          await refetch();
        }
      } catch {
        // do nothing
      }
    },
    [allConversations, filter, queryKey, refetch],
  );

  React.useEffect(() => {
    if (!session) return undefined;

    const { unsubscribe } = session.onMessage(onNewChatMessage);

    return () => {
      unsubscribe();
    };
  }, [onNewChatMessage, session]);

  React.useEffect(() => {
    if (
      !conversationId ||
      !paramConversation ||
      paramConversation.id !== conversationId
    ) {
      return;
    }
    setSelectedConversation(paramConversation);
  }, [conversationId, paramConversation]);

  const itemContent = React.useCallback(
    (index: number) => {
      const conversation = allConversations[index];
      if (!conversation) return null;
      return (
        <InboxMessage
          conversation={conversation}
          onSelect={c => onSelectConversation(c)}
          isSelected={
            !!selectedConversation &&
            selectedConversation.id === conversation.id
          }
        />
      );
    },
    [allConversations, onSelectConversation, selectedConversation],
  );

  return (
    <PageLayout title="Inbox" isLoading={!isLoading && isFetching}>
      <Grid2 container columns={12} spacing={3}>
        <Grid2
          size={{
            xs: 12,
            md: 5,
            lg: 3.5,
          }}
          flex="1 1 auto"
          display={{
            xs: selectedConversation ? 'none' : 'block',
            [BREAKPOINT]: 'block',
          }}
          height={{
            xs: 'calc(100svh - 179px)',
            [BREAKPOINT]: 600,
          }}>
          {!isLoading && <InboxFilters />}

          {error && (
            <Box mb={1}>
              <ColoredAlert
                type="error"
                title="Failed to get conversations"
                subtitle={
                  isAxiosRateLimitError(error as AxiosError)
                    ? 'Too many requests. Please try again in a few minutes.'
                    : 'Please try again later.'
                }
                animate={false}
                dismissible
              />
            </Box>
          )}

          <Virtuoso
            data={allConversations}
            // eslint-disable-next-line react/no-unstable-nested-components
            itemContent={itemContent}
            endReached={() => {
              if (isFetching || !hasNextPage) return;
              fetchNextPage();
            }}
            components={{
              EmptyPlaceholder: isLoading ? undefined : Empty,
              Footer: isFetching ? Loader : undefined,
            }}
          />
        </Grid2>

        <Grid2
          display={{
            xs: selectedConversation ? 'block' : 'none',
            [BREAKPOINT]: 'block',
          }}
          size={{
            xs: 12,
            md: 7,
            lg: 8.5,
          }}
          minHeight={{
            xs: selectedConversation ? 'calc(100vh - 179px)' : 0,
            [BREAKPOINT]: '100%',
          }}
          maxHeight="100%"
          borderRadius="40px"
          overflow="hidden"
          border={`1px solid ${theme.palette.darkerLightGrey.main}`}>
          <ChatBox
            selectedConversation={currentConversation}
            onSelectConversation={onSelectConversation}
            updateSelectedConversation={setSelectedConversation}
            queryKey={queryKey}
          />
        </Grid2>
      </Grid2>
    </PageLayout>
  );
}

export default function Inbox() {
  return (
    <ChatProvider>
      <ChatInbox />
    </ChatProvider>
  );
}
